/**
 * Image URL optimization utility.
 *
 * Appends resize query parameters to CloudFront image URLs so that
 * a CloudFront Function / Lambda@Edge origin-request handler can
 * serve appropriately-sized variants (WebP/AVIF, width-constrained).
 *
 * Query-param contract (consumed by the CDN resize function):
 *   ?w=<width>&h=<height>&f=auto
 *
 * When the CDN handler is not yet deployed the params are ignored
 * and the original image is returned — so this is safe to ship now.
 */

const CLOUDFRONT_PATTERN = /\.cloudfront\.net\//;
const UNSPLASH_PATTERN = /images\.unsplash\.com\//;

/**
 * Build an optimized image URL.
 *
 * @param {string}  src           Original image URL
 * @param {object}  [opts]
 * @param {number}  [opts.width]  Desired display width in CSS pixels
 * @param {number}  [opts.height] Desired display height (defaults to width)
 * @param {string}  [opts.format] 'auto' | 'webp' | 'avif' (default 'auto')
 * @param {number}  [opts.dpr]    Device pixel ratio multiplier (default 2)
 * @returns {string} URL with resize query params (or original if not supported CDN)
 */
export function getOptimizedImageUrl(src, opts = {}) {
  if (!src) return "";

  const dpr = opts.dpr ?? 2;
  const width = opts.width ? Math.round(opts.width * dpr) : undefined;

  if (UNSPLASH_PATTERN.test(src)) {
    const url = new URL(src);
    if (width) url.searchParams.set("w", String(width));
    url.searchParams.set("auto", "format");
    url.searchParams.set("q", "75");
    return url.toString();
  }

  if (!CLOUDFRONT_PATTERN.test(src)) {
    return src;
  }

  const height = opts.height ? Math.round(opts.height * dpr) : width;
  const format = opts.format ?? "auto";

  const url = new URL(src);
  if (width) url.searchParams.set("w", String(width));
  if (height) url.searchParams.set("h", String(height));
  url.searchParams.set("f", format);

  return url.toString();
}

/**
 * Generate a srcSet string for 1x and 2x variants.
 *
 * @param {string}  src
 * @param {number}  displayWidth  CSS pixel width of the element
 * @returns {string} srcSet value  (e.g. "url?w=64 1x, url?w=128 2x")
 */
export function getImageSrcSet(src, displayWidth) {
  if (!src || (!CLOUDFRONT_PATTERN.test(src) && !UNSPLASH_PATTERN.test(src))) {
    return undefined;
  }

  const url1x = getOptimizedImageUrl(src, { width: displayWidth, dpr: 1 });
  const url2x = getOptimizedImageUrl(src, { width: displayWidth, dpr: 2 });

  return `${url1x} 1x, ${url2x} 2x`;
}
